import React, { useCallback, useId } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdContentPaste, MdSwapHoriz } from 'react-icons/md';
import { sendEvent } from '../../common/analytics';
import { useTextCleanerState } from './hooks/useTextCleanerState';
import PresetBar from './components/PresetBar';
import CleanupOptionsPanel from './components/CleanupOptionsPanel';
import TextInputPanel from './components/TextInputPanel';
import TextOutputPanel from './components/TextOutputPanel';
import { sendCleanedTextToEditor } from './textCleanerTransfer';

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

const TextCleaner = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const inputId = useId();
  const outputId = useId();

  const {
    input,
    setInput,
    setInputAndOriginal,
    output,
    stats,
    options,
    setOption,
    activePresetId,
    applyPreset,
    resetOptionsToDefaults,
    clearAll,
    clearSavedState,
    originalInput,
    captureOriginal,
    revertInput,
    swapIo,
  } = useTextCleanerState();

  const showCopyToast = useCallback(
    (title) => {
      toast({
        title,
        status: 'success',
        duration: 2200,
        isClosable: true,
        position: 'bottom',
      });
    },
    [toast]
  );

  const onCopyOutput = useCallback(async () => {
    if (!output) {
      toast({ title: 'Nothing to copy', status: 'info', duration: 2000, position: 'bottom' });
      return;
    }
    const ok = await copyToClipboard(output);
    if (ok) {
      showCopyToast('Copied cleaned text');
      sendEvent('TextCleaner', 'Copy', 'output');
    } else {
      toast({ title: 'Copy failed', description: 'Select output and copy manually.', status: 'error', duration: 3000 });
    }
  }, [output, showCopyToast, toast]);

  const onPasteClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInputAndOriginal(text);
      showCopyToast('Pasted from clipboard');
      sendEvent('TextCleaner', 'Paste', 'clipboard');
    } catch (_) {
      toast({
        title: 'Clipboard access denied',
        description: 'Paste with ⌘V / Ctrl+V or allow clipboard permission.',
        status: 'warning',
        duration: 4000,
        position: 'bottom',
      });
    }
  }, [setInputAndOriginal, showCopyToast, toast]);

  const onDownloadTxt = useCallback(() => {
    if (!output) return;
    const blob = new Blob([output], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cleaned-text.txt';
    a.click();
    URL.revokeObjectURL(url);
    sendEvent('TextCleaner', 'Download', 'txt');
  }, [output]);

  const onSendToEditor = useCallback(() => {
    sendCleanedTextToEditor(output);
    navigate('/texteditor');
    sendEvent('TextCleaner', 'Send', 'texteditor');
  }, [navigate, output]);

  const badgeItems = [
    stats.spacesNormalized > 0 && { key: 'spaces', label: 'Spaces normalized' },
    stats.blankLinesReduced > 0 && { key: 'blanks', label: 'Blank lines reduced' },
    stats.invisibleRemoved > 0 && { key: 'inv', label: 'Invisible chars removed' },
    stats.linesMergedHint > 0 && { key: 'merge', label: 'Lines merged' },
  ].filter(Boolean);

  return (
    <Box maxW="1200px" mx="auto" pb={16}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading as="h1" size="xl" fontWeight="800" letterSpacing="-0.03em" color="gray.800">
            Text Cleaner
          </Heading>
          <Text mt={2} color="gray.600" fontSize="md" maxW="lg" lineHeight="tall">
            Clean messy copied text instantly — fix spacing, line breaks, invisible characters, and formatting issues right
            in your browser.
          </Text>
          <Text mt={1} fontSize="sm" color="gray.500">
            Perfect for text copied from PDFs, emails, websites, Word docs, ChatGPT, and more. Everything stays on your
            device.
          </Text>
        </Box>

        <Box bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="2xl" shadow="sm" p={{ base: 4, md: 6 }}>
          <VStack align="stretch" spacing={6}>
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              gap={6}
              align="stretch"
              sx={{ '& > *': { minW: 0 } }}
            >
              <TextInputPanel value={input} onChange={setInput} labelId={inputId} />
              <TextOutputPanel value={output} labelId={outputId} />
            </Flex>

            <Flex
              direction={{ base: 'column', sm: 'row' }}
              flexWrap="wrap"
              gap={3}
              align={{ base: 'stretch', sm: 'center' }}
              justify="space-between"
            >
              <HStack flexWrap="wrap" spacing={2}>
                <Button colorScheme="teal" size="md" borderRadius="lg" fontWeight="700" onClick={onCopyOutput}>
                  Copy output
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  borderRadius="lg"
                  onClick={async () => {
                    if (!output) {
                      toast({ title: 'Nothing to copy', status: 'info', duration: 2000, position: 'bottom' });
                      return;
                    }
                    const ok = await copyToClipboard(output);
                    if (ok) showCopyToast('Copied as plain text');
                    else toast({ title: 'Copy failed', status: 'error', duration: 3000, position: 'bottom' });
                  }}
                >
                  Copy as plain text
                </Button>
                <Button
                  leftIcon={<MdContentPaste />}
                  variant="outline"
                  size="md"
                  borderRadius="lg"
                  onClick={onPasteClipboard}
                >
                  Paste from clipboard
                </Button>
                <Button leftIcon={<MdSwapHoriz />} variant="outline" size="md" borderRadius="lg" onClick={swapIo} isDisabled={!output}>
                  Use output as input
                </Button>
              </HStack>
              <HStack flexWrap="wrap" spacing={2}>
                <Button variant="outline" size="md" borderRadius="lg" onClick={onSendToEditor} isDisabled={!output}>
                  Send to Text Editor
                </Button>
                <Button variant="outline" size="md" borderRadius="lg" onClick={onDownloadTxt} isDisabled={!output}>
                  Download .txt
                </Button>
              </HStack>
            </Flex>

            <Flex direction={{ base: 'column', md: 'row' }} gap={3} flexWrap="wrap">
              <Button size="sm" variant="ghost" onClick={clearAll}>
                Clear all
              </Button>
              <Button size="sm" variant="ghost" onClick={captureOriginal}>
                Remember current as original
              </Button>
              <Button size="sm" variant="ghost" onClick={revertInput} isDisabled={input === originalInput}>
                Revert input to original
              </Button>
              <Button size="sm" variant="ghost" onClick={resetOptionsToDefaults}>
                Reset options (Smart)
              </Button>
              <Button size="sm" variant="ghost" colorScheme="red" onClick={clearSavedState}>
                Clear saved state
              </Button>
            </Flex>

            <Flex
              direction={{ base: 'column', md: 'row' }}
              gap={2}
              rowGap={1}
              justify="space-between"
              align={{ base: 'flex-start', md: 'center' }}
              flexWrap="wrap"
              lineHeight="1.25"
            >
              <HStack spacing={3} flexWrap="wrap" fontSize="sm" color="gray.600">
                <Text lineHeight="inherit">
                  <Text as="span" fontWeight="700" color="gray.800">
                    {stats.inputChars.toLocaleString()}
                  </Text>{' '}
                  →{' '}
                  <Text as="span" fontWeight="700" color="teal.700">
                    {stats.outputChars.toLocaleString()}
                  </Text>{' '}
                  characters
                </Text>
                <Text lineHeight="inherit">
                  <Text as="span" fontWeight="700" color="gray.800">
                    {stats.inputLines}
                  </Text>{' '}
                  →{' '}
                  <Text as="span" fontWeight="700" color="teal.700">
                    {stats.outputLines}
                  </Text>{' '}
                  lines
                </Text>
                {input && stats.issuesFixed > 0 && (
                  <Badge colorScheme="teal" variant="subtle" borderRadius="md" px={2} py={0.5} lineHeight="1.2">
                    {stats.issuesFixed} improvement{stats.issuesFixed === 1 ? '' : 's'}
                  </Badge>
                )}
              </HStack>
              <HStack spacing={1.5} flexWrap="wrap">
                {badgeItems.map((b) => (
                  <Badge key={b.key} colorScheme="gray" variant="outline" borderRadius="full" fontSize="xs" lineHeight="1.2">
                    {b.label}
                  </Badge>
                ))}
              </HStack>
            </Flex>

            <PresetBar activePresetId={activePresetId} onSelect={applyPreset} />

            <CleanupOptionsPanel options={options} onChange={setOption} />
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default TextCleaner;
